import { Box, Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../api/hooks/useCheckUserPermission";
import useColors from "../../api/hooks/useColors";
import {
  controlKeys,
  remediationAuditLogKeys,
  remediationKeys,
} from "../../api/queryKeys";
import SvgToIcon from "../../icons/SvgToIcon";
import { icons } from "../../scenes/stack/environment/icons/iconExport";
import { PLATFORM_USER_ACTIONS } from "../../utils/constants";
import CustomToolTipWithPermission from "../CustomToolTipWithPermission";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import { Toast } from "../Toast";

export default function RevokeGoogleTokensButton({
  controlId = null,
  tokenToUsersMapping = null,
  showToolTip = true,
  disabled = false,
  ...props
}) {
  const colors = useColors();
  const queryClient = useQueryClient();
  const { revokeGoogleTokens, trackEvent } = useAPI();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.REVOKE_GOOGLE_TOKENS
  );

  const handleRevoke = async () => {
    const message = "User clicked on 'Revoke Google tokens' button";
    trackEvent(message).then();
    setIsDeleteDialogOpen(true);
  };

  const handleRevokeConfirmButton = async () => {
    const message =
      "User clicked on 'Confirm' button for revoking Google tokens";
    trackEvent(message).then();
    Toast.info(`Revoking Google tokens...`);

    try {
      if (tokenToUsersMapping) {
        const revokedTokensCountData = await revokeGoogleTokens(
          controlId,
          tokenToUsersMapping
        );
        const revokedTokensCount = revokedTokensCountData.data;
        const expectedRevokedTokensCount = Object.values(
          tokenToUsersMapping
        ).reduce((acc, curr) => acc + curr.length, 0);

        if (revokedTokensCount === expectedRevokedTokensCount) {
          Toast.success(
            `Successfully revoked ${revokedTokensCount}/${expectedRevokedTokensCount} Google tokens`
          );
        } else if (revokedTokensCount > 0) {
          Toast.info(
            `Revoked ${revokedTokensCount}/${expectedRevokedTokensCount} Google tokens`
          );
        } else {
          Toast.error(
            "Unable to revoke Google tokens. Please contact support for assistance"
          );
        }

        // Refresh the misconfigurations table
        queryClient.invalidateQueries({
          queryKey: controlKeys.all,
        });
        if (controlId) {
          queryClient.invalidateQueries({
            queryKey: remediationAuditLogKeys.auditLogs(controlId),
          });
        }
        queryClient.invalidateQueries({
          queryKey: remediationKeys.all,
        });
      }
    } catch (error) {
      Toast.error("Failed to revoke Google tokens");
    } finally {
      handleCloseDialog();
    }
  };

  const handleCloseDialog = () => {
    const message = "Confirmation dialog for revoke Google tokens was closed";
    trackEvent(message).then();

    setIsDeleteDialogOpen(false);
  };

  return (
    <>
      <CustomToolTipWithPermission
        title={showToolTip ? "Revoke tokens for selected entities" : ""}
        permission={hasPermission}
        placement="top"
      >
        <Box>
          <Button
            sx={{
              color: colors.text_primary,
              backgroundColor: colors.common_white,
              width: "32px",
              height: "32px",
              minWidth: "auto",
              border: "1px solid " + colors.enabled_stroke,
              textTransform: "unset",
              borderRadius: "8px",
              cursor: "pointer",
              ...props.sx,
            }}
            onClick={handleRevoke}
            disabled={disabled || !hasPermission}
          >
            <SvgToIcon
              IconLogo={icons.RevokeTokenIcon}
              style={{
                width: "18px",
                color:
                  disabled || !hasPermission
                    ? colors.action_disabled
                    : colors.icon_action_active,
              }}
            />
          </Button>
        </Box>
      </CustomToolTipWithPermission>

      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        openDialog={isDeleteDialogOpen}
        handleDialogClose={handleCloseDialog}
        handleConfirm={handleRevokeConfirmButton}
        dialogTitle="Revoke Google tokens"
        dialogContentFirstLine="Are you sure you want to revoke the tokens? This action cannot be undone."
        dialogContentSecondLine="The revoked tokens will be removed from the control."
        confirmationButtonText="Revoke"
        destructive={true}
      />
    </>
  );
}
